/**
 * VELYARO — comportements légers du layout "velyaro" / sections Vision360™.
 * S'appuie sur les composants Dawn (pubsub.js, product-info.js, product-form.js) :
 * aucune logique panier/variante n'est dupliquée ici.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const onVariantChange = (callback) => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      return subscribe(PUB_SUB_EVENTS.variantChange, callback);
    }
    return () => {};
  };

  /* ------------------------------------------------------------------
   * Template alternatif (?view=…) : conserve le paramètre lors des
   * changements de variante pour que la Section Rendering API et l'URL
   * restent sur le template Vision360™ (utile en prévisualisation).
   * ------------------------------------------------------------------ */
  const initialView = new URLSearchParams(window.location.search).get('view');
  customElements.whenDefined('product-info').then(() => {
    const proto = customElements.get('product-info').prototype;
    if (proto.__v360Patched) return;
    proto.__v360Patched = true;

    const buildRequestUrl = proto.buildRequestUrlWithParams;
    proto.buildRequestUrlWithParams = function (url, optionValues, shouldFetchFullPage) {
      const requestUrl = buildRequestUrl.call(this, url, optionValues, shouldFetchFullPage);
      const view = this.dataset.v360View;
      if (!view) return requestUrl;
      return `${requestUrl}${requestUrl.includes('?') ? '&' : '?'}view=${encodeURIComponent(view)}`;
    };

    const updateURL = proto.updateURL;
    proto.updateURL = function (url, variantId) {
      updateURL.call(this, url, variantId);
      if (!initialView || !this.dataset.v360View || this.dataset.updateUrl === 'false') return;
      const next = new URL(window.location.href);
      next.searchParams.set('view', initialView);
      window.history.replaceState({}, '', next);
    };
  });

  /* ------------------------------------------------------------------
   * Galerie produit : scroll-snap natif + points / miniatures / flèches.
   * ------------------------------------------------------------------ */
  class V360Gallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.v360-gallery__track');
      this.slides = this.track ? Array.from(this.track.querySelectorAll('.v360-gallery__slide')) : [];
      if (this.slides.length < 2) return;

      this.index = 0;
      this.navButtons = Array.from(this.querySelectorAll('[data-index]'));
      this.arrows = Array.from(this.querySelectorAll('[data-dir]'));
      this.counter = this.querySelector('[data-current]');

      this.navButtons.forEach((button) =>
        button.addEventListener('click', () => this.goTo(Number(button.dataset.index)))
      );
      this.arrows.forEach((arrow) =>
        arrow.addEventListener('click', () => this.goTo(this.index + Number(arrow.dataset.dir)))
      );
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(this.index + 1);
        if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
      });

      let ticking = false;
      this.track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            this.syncFromScroll();
          });
        },
        { passive: true }
      );

      this.unsubscribe = onVariantChange(({ data }) => {
        if (data.sectionId !== this.dataset.section) return;
        const mediaId = data.variant?.featured_media?.id;
        if (!mediaId) return;
        const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (target > -1) this.goTo(target);
      });

      this.render();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    get step() {
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft || this.track.clientWidth;
    }

    syncFromScroll() {
      const next = Math.round(this.track.scrollLeft / this.step);
      if (next !== this.index) {
        this.index = Math.max(0, Math.min(next, this.slides.length - 1));
        this.render();
      }
    }

    goTo(index) {
      const target = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({
        left: this.slides[target].offsetLeft - this.slides[0].offsetLeft,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.index = target;
      this.render();
    }

    render() {
      this.navButtons.forEach((button) => {
        const active = Number(button.dataset.index) === this.index;
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(this.index + 1);
      this.arrows.forEach((arrow) => {
        const dir = Number(arrow.dataset.dir);
        arrow.disabled = (dir < 0 && this.index === 0) || (dir > 0 && this.index === this.slides.length - 1);
      });
      this.slides.forEach((slide, i) => {
        if (i === this.index) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    }
  }

  if (!customElements.get('v360-gallery')) customElements.define('v360-gallery', V360Gallery);

  /* ------------------------------------------------------------------
   * Buy box : prix partagé, libellé du CTA, barre d'achat mobile.
   * ------------------------------------------------------------------ */
  const readState = (root) => {
    try {
      return JSON.parse(root.querySelector('[data-v360-state]')?.textContent || 'null');
    } catch {
      return null;
    }
  };

  const initBuyBox = (hero) => {
    if (hero.dataset.v360Ready) return;
    hero.dataset.v360Ready = 'true';
    const sectionId = hero.dataset.section;
    const getMainButton = () => document.getElementById(`ProductSubmitButton-${sectionId}`);
    const bar = hero.querySelector('[data-v360-sticky]');
    const stickyButton = bar?.querySelector('[data-v360-sticky-add]');

    // Réapplique le libellé personnalisé (Dawn remet la traduction par défaut après un changement de variante).
    const syncButtons = () => {
      const main = getMainButton();
      if (!main) return;
      const label = main.querySelector('span');
      const disabled = main.hasAttribute('disabled');
      const addLabel = main.dataset.addLabel;
      const defaultAdd = window.variantStrings?.addToCart;
      if (label) {
        // Dawn réécrit le libellé avec les traductions de la langue de la boutique : on réapplique les nôtres.
        const current = label.textContent.trim();
        const replacements = {
          [defaultAdd]: addLabel,
          [window.variantStrings?.soldOut]: main.dataset.soldOutLabel,
          [window.variantStrings?.unavailable]: main.dataset.unavailableLabel,
        };
        const next = replacements[current];
        if (next && next !== current) label.textContent = next;
      }

      if (!stickyButton) return;
      const mainText = label ? label.textContent.trim() : '';
      const isAddText = mainText === addLabel || mainText === defaultAdd;
      stickyButton.disabled = disabled;
      stickyButton.textContent = !disabled || isAddText ? stickyButton.dataset.addLabel : mainText || stickyButton.dataset.soldOutLabel;
      stickyButton.setAttribute('aria-busy', String(main.classList.contains('loading')));
    };

    const observeMainButton = () => {
      const main = getMainButton();
      if (!main) return;
      new MutationObserver(syncButtons).observe(main, {
        attributes: true,
        attributeFilter: ['disabled', 'class', 'aria-disabled'],
      });
      syncButtons();
    };
    observeMainButton();

    onVariantChange(({ data }) => {
      if (data.sectionId !== sectionId) return;
      const state = readState(data.html);
      if (state) {
        document.querySelectorAll('[data-v360-price]').forEach((el) => (el.textContent = state.price));
        hero.querySelectorAll('[data-v360-variant-title]').forEach((el) => (el.textContent = state.title));
        const current = hero.querySelector('[data-v360-state]');
        if (current) current.textContent = JSON.stringify(state);
      }
      const stock = hero.querySelector(`#Inventory-${sectionId}`);
      const nextStock = data.html.getElementById(`Inventory-${sectionId}`);
      if (stock && nextStock) stock.className = nextStock.className;
      syncButtons();
    });

    if (!bar || !stickyButton) return;

    stickyButton.addEventListener('click', () => {
      const main = getMainButton();
      if (!main || main.hasAttribute('disabled') || main.getAttribute('aria-disabled') === 'true') return;
      main.click();
    });

    const mobile = window.matchMedia('(max-width: 989px)');
    const form = hero.querySelector('.v360-form');
    const finalCta = document.querySelector('[data-v360-final]');
    let ctaPassed = false;
    let finalVisible = false;

    const update = () => {
      const show = mobile.matches && ctaPassed && !finalVisible;
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
      document.body.classList.toggle('v360-has-sticky', mobile.matches);
    };

    if ('IntersectionObserver' in window && form) {
      new IntersectionObserver(([entry]) => {
        ctaPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        update();
      }).observe(form);

      if (finalCta) {
        new IntersectionObserver(([entry]) => {
          finalVisible = entry.isIntersecting;
          update();
        }).observe(finalCta);
      }
    }
    mobile.addEventListener?.('change', update);
    update();
  };

  /* ------------------------------------------------------------------
   * Liens internes (CTA final → buy box) : défilement + focus accessible.
   * ------------------------------------------------------------------ */
  const initScrollLinks = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-v360-scroll]');
      if (!link) return;
      const id = decodeURIComponent(link.hash.slice(1));
      const target = id && document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  };

  /* ------------------------------------------------------------------
   * Vidéos de section : lecture uniquement à l'écran, jamais si
   * l'utilisateur préfère réduire les animations.
   * ------------------------------------------------------------------ */
  const initAutoplayVideos = () => {
    const videos = document.querySelectorAll('[data-v-autoplay] video');
    if (!videos.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      videos.forEach((video) => (video.controls = true));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) target.play().catch(() => (target.controls = true));
          else target.pause();
        }),
      { rootMargin: '100px 0px' }
    );
    videos.forEach((video) => observer.observe(video));
  };

  /* Décalage du header collant (ancres / buy box sticky). */
  const syncHeaderOffset = () => {
    const header = document.querySelector('.v-header-section, .section-header');
    if (header) document.body.style.setProperty('--v-header-offset', `${header.offsetHeight}px`);
  };

  /* ------------------------------------------------------------------
   * Menu mobile de l'en-tête VELYARO (<details> natif) :
   * fermeture au clic sur un lien (ancres) et à la touche Échap.
   * ------------------------------------------------------------------ */
  const initHeaderMenu = () => {
    document.querySelectorAll('[data-v-menu]').forEach((menu) => {
      if (menu.dataset.vReady) return;
      menu.dataset.vReady = 'true';
      const summary = menu.querySelector('summary');
      summary?.setAttribute('aria-expanded', 'false');
      menu.addEventListener('toggle', () => summary?.setAttribute('aria-expanded', String(menu.open)));
      menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => (menu.open = false)));
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !menu.open) return;
        menu.open = false;
        summary?.focus();
      });
      document.addEventListener('click', (event) => {
        if (menu.open && !menu.contains(event.target)) menu.open = false;
      });
    });
  };

  /* ------------------------------------------------------------------
   * Apparition discrète au scroll (accueil + page produit VELYARO™).
   * - Les éléments sont choisis ici (liste unique) ; [data-v-reveal] = « révéler ses enfants ».
   * - Le masquage n'est posé QUE par ce script, et seulement sous la ligne de flottaison :
   *   sans JS, en cas d'erreur ou si le mouvement est réduit, tout reste visible.
   * - Une seule fois par élément ; à la fin, les classes sont retirées (état d'origine intact).
   * - Exclus : hero, galerie / buy box produit, widget Judge.me, header, footer.
   * ------------------------------------------------------------------ */
  const REVEAL_ITEMS = [
    '[data-v-reveal] > *',
    '.v-section__head > *',
    '.v360-feature__media',
    '.v360-feature__content > *',
    '.vh-cases__scroller',
    '.vh-essentials__media',
    '.vh-length',
    '.vh-reviews__cta',
    '.v-accordion__item',
    '.v360-faq__more',
    '.vh-final__media',
    '.v360-card',
    '.v360-steps__item',
    '.v360-compare__wrap',
    '.v360-reviews__summary',
    '.v360-final__inner > *',
  ].join(',');
  const REVEAL_EXCLUDED = '.vh-hero, .v360-section--hero, .v360-reviews__app, [class*="jdgm"]';
  const REVEAL_STAGGER = 90; // ms entre éléments apparaissant ensemble
  const REVEAL_MAX_STEPS = 4;

  const initReveal = () => {
    const main = document.getElementById('MainContent');
    if (!main || !main.querySelector('.vh-hero, .v360-section--hero')) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) return;

    const candidates = Array.from(main.querySelectorAll(REVEAL_ITEMS)).filter(
      (el) => !el.closest(REVEAL_EXCLUDED) && !el.classList.contains('v-reveal')
    );
    // Un élément dont un parent est déjà animé ne l'est pas une seconde fois.
    const items = candidates.filter((el) => !candidates.some((other) => other !== el && other.contains(el)));
    // Contenu déjà à l'écran au chargement : jamais masqué.
    const below = items.filter((el) => el.getBoundingClientRect().top >= window.innerHeight);
    if (!below.length) return;

    const cleanup = (el) => {
      el.classList.remove('v-reveal', 'is-revealed');
      el.style.removeProperty('--v-reveal-delay');
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left)
          .forEach(({ target }, index) => {
            observer.unobserve(target);
            const delay = Math.min(index, REVEAL_MAX_STEPS) * REVEAL_STAGGER;
            if (delay) target.style.setProperty('--v-reveal-delay', `${delay}ms`);
            target.classList.add('is-revealed');
            const done = () => {
              target.removeEventListener('transitionend', onEnd);
              cleanup(target);
            };
            const onEnd = (event) => event.target === target && done();
            target.addEventListener('transitionend', onEnd);
            setTimeout(done, delay + 900);
          });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );

    below.forEach((el) => {
      el.classList.add('v-reveal');
      observer.observe(el);
    });
    document.documentElement.classList.add('v-reveal-ready');

    const revealAll = () => document.querySelectorAll('.v-reveal').forEach(cleanup);
    window.addEventListener('beforeprint', revealAll);
    reducedMotion.addEventListener?.('change', revealAll);
  };

  /* ------------------------------------------------------------------
   * Avis Judge.me (section v360-reviews)
   * 1. Répartition des notes : lue dans les données que Judge.me injecte sur la page
   *    (jdgm.data.reviewWidget[id].histogram). Rien n'est calculé ni codé en dur ;
   *    sans données, la liste reste masquée.
   * 2. Avis importés sans commentaire : leur texte est seulement le nom du produit.
   *    Ce texte (égalité stricte) est masqué à l'affichage ; note, auteur et photos restent visibles.
   *    Les données Judge.me ne sont pas modifiées.
   * ------------------------------------------------------------------ */
  const initReviewsHistogram = () => {
    document.querySelectorAll('[data-v360-histogram]').forEach((summary) => {
      const list = summary.querySelector('[data-v360-histogram-list]');
      const data = window.jdgm?.data?.reviewWidget?.[summary.dataset.productId];
      const rows = Array.isArray(data?.histogram) ? data.histogram : null;
      if (!list || !rows || !rows.length || list.childElementCount) return;
      const label = list.dataset.countLabel || 'avis';
      rows
        .slice()
        .sort((a, b) => Number(b.rating) - Number(a.rating))
        .forEach((row) => {
          const li = document.createElement('li');
          li.className = 'v360-reviews__bar';
          li.setAttribute('aria-label', `${row.rating} ★ : ${row.frequency} ${label}`);
          li.innerHTML =
            `<span class="v360-reviews__bar-label" aria-hidden="true">${Number(row.rating)}<svg class="v360-reviews__bar-star" viewBox="0 0 24 24" focusable="false"><path d="M12 2.6l2.92 5.92 6.53.95-4.73 4.61 1.12 6.5L12 17.51l-5.84 3.07 1.12-6.5-4.73-4.61 6.53-.95z"/></svg></span>` +
            `<span class="v360-reviews__bar-track" aria-hidden="true"><span class="v360-reviews__bar-fill" style="width:${Math.max(0, Math.min(100, Number(row.percentage) || 0))}%"></span></span>` +
            `<span class="v360-reviews__bar-count" aria-hidden="true">${Number(row.frequency) || 0}</span>`;
          list.appendChild(li);
        });
      list.hidden = false;
      summary.classList.add('has-histogram');
    });
  };

  const initReviewPlaceholders = () => {
    document.querySelectorAll('[data-v360-hide-placeholder]').forEach((container) => {
      if (container.dataset.v360PlaceholderReady) return;
      container.dataset.v360PlaceholderReady = 'true';
      const placeholder = container.dataset.v360HidePlaceholder.trim();
      if (!placeholder) return;
      const apply = () => {
        container.querySelectorAll('.jdgm-review-content__body-content').forEach((body) => {
          const isPlaceholder = body.textContent.trim() === placeholder;
          const wrapper = body.closest('.jm-review-body-wrapper') || body;
          wrapper.classList.toggle('v360-review-placeholder', isPlaceholder);
        });
      };
      apply();
      let scheduled = false;
      new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          apply();
        });
      }).observe(container, { childList: true, subtree: true });
    });
  };

  const init = () => {
    document.querySelectorAll('product-info.v360-hero').forEach(initBuyBox);
    initReviewsHistogram();
    window.addEventListener('load', initReviewsHistogram, { once: true });
    initReviewPlaceholders();
    initScrollLinks();
    initAutoplayVideos();
    initHeaderMenu();
    initReveal();
    syncHeaderOffset();
    window.addEventListener('resize', syncHeaderOffset, { passive: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('product-info.v360-hero').forEach(initBuyBox);
    initAutoplayVideos();
    initHeaderMenu();
    document.querySelectorAll('.v-reveal').forEach((item) => item.classList.remove('v-reveal', 'is-revealed'));
  });
})();
